import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon, Search, Users, Trophy, Calendar, Target, Zap, Database, AlertTriangle } from 'lucide-react';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  variant?: 'default' | 'search' | 'error' | 'coming-soon';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon: IconComponent,
  title,
  description,
  action,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  // Default icons for different variants
  const getDefaultIcon = () => {
    switch (variant) {
      case 'search': return Search;
      case 'error': return AlertTriangle;
      case 'coming-soon': return Calendar;
      default: return Database;
    }
  };

  const Icon = IconComponent || getDefaultIcon();

  const sizeClasses = {
    sm: {
      container: 'p-8',
      icon: 'w-12 h-12',
      title: 'text-lg',
      description: 'text-sm',
    },
    md: {
      container: 'p-12',
      icon: 'w-16 h-16',
      title: 'text-xl',
      description: 'text-base',
    },
    lg: {
      container: 'p-16',
      icon: 'w-20 h-20',
      title: 'text-2xl',
      description: 'text-lg',
    },
  };

  const variantStyles = {
    default: {
      icon: 'text-gray-600',
      background: 'glass-card',
    },
    search: {
      icon: 'text-blue-400',
      background: 'glass-card border-blue-500/20',
    },
    error: {
      icon: 'text-red-400',
      background: 'glass-card border-red-500/20',
    },
    'coming-soon': {
      icon: 'text-primary',
      background: 'glass-card border-primary/20',
    },
  };

  const style = variantStyles[variant];
  const sizing = sizeClasses[size];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`${style.background} ${sizing.container} text-center ${className}`}
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="relative inline-block mb-6"
      >
        <Icon className={`${sizing.icon} ${style.icon} mx-auto`} />

        {/* Animated background glow */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className={`absolute inset-0 ${style.icon.replace('text-', 'bg-')}/20 blur-xl rounded-full`}
        />
      </motion.div>

      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={`${sizing.title} font-bold text-white mb-3`}
      >
        {title}
      </motion.h3>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className={`${sizing.description} text-gray-400 max-w-md mx-auto mb-6`}
      >
        {description}
      </motion.p>

      {action && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={action.onClick}
          className="btn-primary inline-flex items-center space-x-2 group"
        >
          <span>{action.label}</span>
          <motion.div
            className="group-hover:translate-x-1 transition-transform"
          >
            <Target className="w-4 h-4" />
          </motion.div>
        </motion.button>
      )}

      {/* Floating particles animation for visual interest */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-primary/20 rounded-full"
            style={{
              left: `${20 + i * 30}%`,
              top: `${30 + i * 20}%`,
            }}
            animate={{
              y: [-10, -20, -10],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Preset empty states for common scenarios
const NoTeams: React.FC<Partial<EmptyStateProps>> = (props) => (
  <EmptyState
    icon={Users}
    title="No Teams Found"
    description="Try adjusting your search filters or check back later for new teams."
    variant="search"
    {...props}
  />
);

const NoPlayers: React.FC<Partial<EmptyStateProps>> = (props) => (
  <EmptyState
    icon={Users}
    title="No Players Found"
    description="No players match your current search criteria. Try adjusting your filters."
    variant="search"
    {...props}
  />
);

const NoGames: React.FC<Partial<EmptyStateProps>> = (props) => (
  <EmptyState
    icon={Trophy}
    title="No Games Found"
    description="There are no games scheduled for your selected criteria."
    variant="search"
    {...props}
  />
);

const NoChampions: React.FC<Partial<EmptyStateProps>> = (props) => (
  <EmptyState
    icon={Trophy}
    title="No Champions Found"
    description="No championship data available for the selected timeframe."
    variant="search"
    {...props}
  />
);

interface LoadingErrorProps {
  error?: string;
  onRetry?: () => void;
}

const LoadingError: React.FC<LoadingErrorProps> = ({ error, onRetry }) => (
  <EmptyState
    icon={AlertTriangle}
    title="Failed to Load Data"
    description={error || "Something went wrong while loading the data. Please try again."}
    variant="error"
    action={onRetry ? { label: "Try Again", onClick: onRetry } : undefined}
  />
);

interface ComingSoonProps extends Partial<EmptyStateProps> {
  feature: string;
}

const ComingSoon: React.FC<ComingSoonProps> = ({ feature, ...props }) => (
  <EmptyState
    icon={Zap}
    title="Coming Soon"
    description={`${feature} will be available in a future update. Stay tuned for more exciting features!`}
    variant="coming-soon"
    {...props}
  />
);

const NoData: React.FC<Partial<EmptyStateProps> & { message?: string }> = ({ message, ...props }) => (
  <EmptyState
    icon={Database}
    title="No Data Available"
    description={message || "No data is available at this time."}
    variant="default"
    {...props}
  />
);

export const EmptyStates = {
  NoTeams,
  NoPlayers,
  NoGames,
  NoChampions,
  NoData,
  LoadingError,
  ComingSoon,
};

export default EmptyState;